import cn from 'classnames';
import { useKeenSlider } from 'keen-slider/react';
import Image from 'next/image';
import Link from 'next/link';
import 'keen-slider/keen-slider.min.css';
import Section from 'react-scale/components/elements/section';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import showModal from 'react-scale/components/tailwind/Modal/showModal';
import { VideoOverlay } from 'react-scale/components/elements/video-modal';
import { WheelControls } from 'react-scale/lib/keen-slider';

const Events: FC<{ events: { event: Event }[] }> = ({ events }) => {
  const { isShowing, toggle, close } = showModal();
  const videoRef = useRef();
  const [loaded, setLoaded] = useState(false);
  const [URL, setURL] = useState('');
  const sliderParentRef = useRef<HTMLDivElement>();
  const [sliderRef, slider] = useKeenSlider(
    {
      initial: 0,
      mode: 'snap',
      rubberband: false,
      slides: {
        perView: 1.05,
        spacing: 16
      },
      breakpoints: {
        '(min-width: 640px)': {
          mode: 'free',
          slides: {
            perView: 1.3,
            spacing: 24
          }
        },
        '(min-width: 768px)': {
          mode: 'free',
          slides: {
            perView: 2,
            spacing: 32
          }
        },
        '(min-width: 1280px)': {
          mode: 'free',
          slides: {
            perView: 2.5,
            spacing: 32
          }
        }
      },
      dragStarted: () => (slider.current.container.style.cursor = 'grabbing'),
      dragEnded: () => (slider.current.container.style.cursor = 'grab'),
      created: () => setLoaded(true)
    },
    [WheelControls]
  );

  useEffect(() => {
    if (!sliderParentRef.current) return;

    const handleTab = (e: KeyboardEvent): void => {
      if (e.key === 'Tab') {
        if (e.shiftKey) slider.current.prev();
        else slider.current.next();
      }
    };
    const handleFocus = (e: FocusEvent): void => {
      const targetIsFirst = e.target === slider.current.slides[0];
      const targetIsLast = e.target === slider.current.slides[slider.current.slides.length - 1];

      if (targetIsFirst) slider.current.moveToIdx(slider.current.track.details.minIdx);
      if (targetIsLast) slider.current.moveToIdx(slider.current.track.details.maxIdx);
    };

    sliderParentRef.current.addEventListener('keydown', handleTab);
    sliderParentRef.current.addEventListener('focusin', handleFocus);

    return () => {
      if (sliderParentRef.current) {
        sliderParentRef.current.removeEventListener('keydown', handleTab);
        sliderParentRef.current.removeEventListener('focusin', handleFocus);
      }
    };
  }, []);

  return (
    <Section overflowHidden paddingTop={false}>
      <div ref={sliderParentRef}>
        <div
          ref={sliderRef}
          className={cn(
            'keen-slider !overflow-visible cursor-grab h-[167px] xl:h-[192px] transition-opacity duration-300',
            loaded ? 'opacity-100 visible' : 'opacity-0 invisible'
          )}
        >
          {events.map(({ event }, index) => {
            return (
              <EventCard
                key={index}
                onClick={() => {
                  toggle();
                  setURL(event?.value.data.link);
                }}
                value={{
                  data: {
                    overtitle: event?.value.data.overtitle,
                    title: event?.value.data.title,
                    author: event?.value.data.author,
                    link: event?.value.data.link
                  }
                }}
              />
            );
          })}
        </div>
      </div>

      <VideoOverlay
        ref={videoRef}
        // @ts-ignore
        isShowing={isShowing}
        close={close}
        time
        url={URL}
        hideCloseButton
      />
    </Section>
  );
};

export type Event = {
  onClick?: () => void;
  value: {
    data: {
      overtitle: string;
      title: string | ReactNode;
      author: { name: string; image: { src: string; alt: string } };
      link: string;
    };
  };
};

const EventCard: FC<Event> = ({
  onClick,
  value: {
    data: { title, overtitle, author, link }
  }
}) => {
  return !onClick ? (
    <Link
      href={link}
      target="_blank"
      className="keen-slider__slide border border-[#232323] rounded-lg relative hover:border-white/30 transition-colors duration-700 group"
    >
      <EventBody
        value={{
          data: {
            title,
            overtitle,
            author,
            link
          }
        }}
      />
    </Link>
  ) : (
    <button
      onClick={onClick}
      className="keen-slider__slide border border-[#232323] rounded-lg relative hover:border-white/30 transition-colors duration-700 group text-left"
    >
      <EventBody
        value={{
          data: {
            title,
            overtitle,
            author,
            link
          }
        }}
      />
    </button>
  );
};

const EventBody: FC<Pick<Event, 'value'>> = ({
  value: {
    data: { title, overtitle, author }
  }
}) => {
  return (
    <>
      <div
        className="flex items-center justify-start transition-colors bg-black rounded-lg group-hover:bg-neutral-950"
        style={{
          backgroundImage:
            'radial-gradient(63.24% 63.24% at 50% 50%, #171717 0%, rgba(23, 23, 23, 0) 100%)'
        }}
      >
        <Image
          src={author?.image.src}
          alt={author?.image.alt}
          width={190}
          height={190}
          quality={100}
          className="max-w-[165px] xl:max-w-max"
        />
        <div className="flex flex-col lg:pr-10">
          <p className="mb-1 text-xs font-medium sm:text-sm text-scale-lightmauve">{overtitle}</p>
          <p className="font-aeonik sm:text-lg md:text-xl md:leading-normal lg:text-2xl lg:leading-[111%] mb-2">
            {title}
          </p>
          <p className="text-xs font-medium md:text-sm text-white/60">by {author?.name}</p>
        </div>
      </div>

      <div
        tabIndex={-1}
        role="none"
        className="absolute items-center justify-center hidden w-8 h-8 bg-white rounded-full pointer-events-none xl:flex right-3 bottom-3 md:right-4 md:bottom-4 sm:w-10 sm:h-10"
      >
        <svg
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-3 h-3 sm:w-4 sm:h-4"
          xmlSpace="preserve"
        >
          <path
            d="M12.5 7.1 4.6 2.4C4 2.1 3 2.4 3 3.4v9.3c0 .6.5 1.1 1.1 1.1.2 0 .4-.1.5-.2L12.5 9c.5-.3.7-1 .4-1.5-.1-.2-.3-.3-.4-.4z"
            fill="#000"
          />
        </svg>
      </div>
    </>
  );
};

export default Events;
